<script setup lang="ts">
import isPDFFile from "@/helpers/files/isPDFFile";
import { type IFileState, type IValidationResult } from "@/interfaces";
import ButtonEllipsis from "@/stories/buttons/ButtonEllipsis/ButtonEllipsis.vue";
import DropdownLink from "@/stories/buttons/DropdownButton/DropdownLink.vue";
import DropdownTrigger from "@/stories/buttons/DropdownTrigger/DropdownTrigger.vue";
import FileItem from "@/stories/displays/FileGallery/FileItem.vue";
import ImageItem from "@/stories/displays/FileGallery/ImageItem.vue";
import PDFItem from "@/stories/displays/FileGallery/PDFItem.vue";
import TextInput from "@/stories/form/TextInput/TextInput.vue";
import { computed, toRefs } from "vue";

interface Props {
  file: IFileState;
  preview: boolean;
  favorite: boolean;
  deletable: boolean;
  nameEditing?: boolean;
  validation?: IValidationResult | null;
  textInputName?: string;
  showFavoriteIcon?: boolean;
  customStyles?: string;
  showDropdownMenu?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  nameEditing: false,
  showFavoriteIcon: true,
  customStyles: "min-w-160 w-160 h-160 min-h-160 rounded-2xl",
  showDropdownMenu: true,
});

const { file } = toRefs(props);

defineEmits<{
  click: [];
  delete: [];
  favorite: [];
  edit: [];
}>();

const hasInputError = computed(() => {
  return file.value.name?.trim().length < 1;
});

const download = () => {
  if (file.value.url) {
    window.open(file.value.url, "_blank");
  }
};

const isPDF = computed(() => isPDFFile(file.value.type));
</script>

<template>
  <div
    class="flex gap-6 items-center"
    :class="{ 'justify-start w-full': nameEditing }"
  >
    <div class="group relative">
      <ImageItem
        v-if="file.type === 'image'"
        :file="file"
        :customStyles="customStyles"
        @click="$emit('click')"
      />
      <PDFItem v-else-if="isPDF" :file="file" :customStyles="customStyles" />
      <FileItem
        v-else
        :file="file"
        :customStyles="customStyles"
        @click="$emit('click')"
      />

      <div class="absolute left-0 top-0 w-full py-2 px-2 z-10">
        <div
          class="flex items-center z-50"
          :class="{
            'justify-between': file.isFavorite && showFavoriteIcon,
            'justify-end': !(file.isFavorite && showFavoriteIcon),
          }"
        >
          <div
            v-if="file.isFavorite && showFavoriteIcon"
            class="bg-white rounded-full w-8 h-8 min-w-8 min-h-8 border border-neutral300 shadow-small flex justify-center items-center"
          >
            <i class="fas fa-star text-yellow-300"></i>
          </div>
          <DropdownTrigger
            v-if="showDropdownMenu"
            :saveButtonDisplayment="'hide'"
          >
            <template #trigger>
              <ButtonEllipsis />
            </template>

            <template #content>
              <DropdownLink
                v-if="file.type === 'image'"
                svg="far-pen-to-square"
                @click="$emit('edit')"
              >
                {{ $t("global.edit") }}
              </DropdownLink>

              <DropdownLink
                v-if="file.url"
                svg="far-download"
                @click="download"
              >
                {{ $t("global.download") }}
              </DropdownLink>

              <DropdownLink
                v-if="favorite && !file.isFavorite"
                svg="far-image"
                @click="$emit('favorite')"
              >
                {{ $t("global.use_as_front_image") }}
              </DropdownLink>

              <DropdownLink
                svg="far-trash-can"
                text-color="text-red-500"
                @click="$emit('delete')"
              >
                {{ $t("global.delete") }}
              </DropdownLink>
            </template>
          </DropdownTrigger>
        </div>
      </div>
    </div>
    <div v-if="nameEditing" class="flex-grow">
      <TextInput
        :label="$t('global.title')"
        :name="textInputName || 'filename'"
        :description="
          $t('global.validation_length', {
            rule: 50,
            current: 50 - (file.name?.length || 0),
          })
        "
        is-required
        :maxlength="50"
        :validation="validation"
        :error="hasInputError"
        v-model="file.name"
      />
    </div>
  </div>
</template>
